import React, { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import {Config} from "plotly.js";
import PlotlyComponent from "react-plotly.js";
import {LegendColors} from "../../../utils/common";

const Plot = PlotlyComponent as any;

export interface IRoseChartProps {
  data: { [key: string]: any }[];
  series: string[];
  angle: number;
  unitX: string;
  downloadName?: string;
}

interface PlotData {
  type: 'barpolar';
  name: string;
  r: any[];
  theta: any[];
  hovertemplate: string;
  showlegend: boolean;
  opacity: number;
  marker: {
    color: string;
  };
}

const interpolateColor = (color1: string, color2: string, factor: number) => {
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');

  const r1 = parseInt(hex1.slice(0, 2), 16);
  const g1 = parseInt(hex1.slice(2, 4), 16);
  const b1 = parseInt(hex1.slice(4, 6), 16);

  const r2 = parseInt(hex2.slice(0, 2), 16);
  const g2 = parseInt(hex2.slice(2, 4), 16);
  const b2 = parseInt(hex2.slice(4, 6), 16);

  const r = Math.round(r1 + factor * (r2 - r1));
  const g = Math.round(g1 + factor * (g2 - g1));
  const b = Math.round(b1 + factor * (b2 - b1));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const getColorForSeries = (index: number, totalSeries: number): string => {
  const reversedColors = [...LegendColors].reverse();

  if (totalSeries === 1) return reversedColors[0];

  const position = index / (totalSeries - 1) * (reversedColors.length - 1);
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.min(lowerIndex + 1, reversedColors.length - 1);

  return interpolateColor(
    reversedColors[lowerIndex],
    reversedColors[upperIndex],
    position - lowerIndex
  );
};

const RoseChart: React.FC<IRoseChartProps> = ({
    data,
    series,
    angle,
    unitX,
    downloadName = 'Downloaded Chart'
  }) => {
  const [loading, setLoading] = useState(false);

  const transformData = (): PlotData[] => {
    return series.map((seriesName, index) => {
      return {
        type: 'barpolar',
        name: seriesName,
        r: data.map(item => item[seriesName]),
        theta: data.map(item => item.category),
        hovertemplate: `${seriesName}: %{r}${unitX}<extra></extra>`,
        showlegend: true,
        opacity: 0.8,
        marker: {
          color: getColorForSeries(index, series.length)
        }
      };
    });
  };

  const layout: Partial<any> = {
    grid: {
      rows: 1,
      columns: 1,
      height: 1000
    },
    polar: {
      barmode: 'stack',
      bargap: 0.1,
      angularaxis: {
        direction: 'clockwise',
        rotation: 90,
      },
      radialaxis: {
        ticksuffix: unitX,
        showticklabels: true,
      },
    },
    showlegend: true,
    legend: {
      orientation: 'h',
      yanchor: 'top',
      y: -0.1,
      xanchor: 'center',
      x: 0.5,
      tracegroupgap: 0.5,
      traceorder: 'normal',
      itemsizing: 'constant',
    },
    margin: {
      t: 30,
      b: 80,
      l: 50,
      r: 50
    },
    autosize: true
  };

  const config: Partial<Config> = {
    responsive: true,
    displayModeBar: true,
    toImageButtonOptions: {
      format: 'png' as 'png' | 'svg' | 'jpeg' | 'webp',
      filename: downloadName,
      scale: 2
    },
    displaylogo: false,
    modeBarButtonsToRemove: ['lasso2d', 'select2d']
  };

  const debouncedUpdate = debounce(() => {
    setLoading(false);
  }, 50);

  useEffect(() => {
    setLoading(true);
    debouncedUpdate();
    return () => {
      debouncedUpdate.cancel();
    };
  }, [data, series, angle]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)'
          }}
        >
          Loading...
        </div>
      )}
      <Plot
        data={transformData()}
        layout={layout}
        config={config}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default RoseChart;
